import React, { useEffect, useState } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import apiEmpresa from '../../api/apiEmpresa';
import { DadosPublicosEmpresa } from '../../Entity/Empresa';
import Loader from '../../Components/generic/Loader';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      padding: theme.spacing(3)
    },
    content: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      width: '100%',
      height: '100%'
    },
    policyCard: {
      background: '#ffffff',
      color: '#333333',
      padding: theme.spacing(4),
      marginTop: theme.spacing(4),
      borderRadius: 8,
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      maxWidth: '800px',
      width: '100%',
      '& h1': {
        color: '#2c3e50',
        fontSize: '2rem',
        marginBottom: theme.spacing(3),
        borderBottom: `2px solid ${theme.palette.primary.main}`,
        paddingBottom: theme.spacing(1)
      },
      '& h2': {
        color: '#34495e',
        fontSize: '1.3rem',
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(1)
      },
      '& p': {
        lineHeight: 1.6,
        marginBottom: theme.spacing(2),
        textAlign: 'justify'
      },
      '& ul': {
        paddingLeft: theme.spacing(3),
        marginBottom: theme.spacing(2)
      },
      '& li': {
        marginBottom: theme.spacing(1),
        lineHeight: 1.5
      },
      '& a': {
        color: theme.palette.primary.main,
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline'
        }
      }
    }
  })
);

const PoliticasPrivacidadePage: React.FC = () => {
  const classes = useStyles();
  const [empresa, setEmpresa] = useState<DadosPublicosEmpresa | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDadosEmpresa = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiEmpresa.getDadosPublicosEmpresa();
        setEmpresa(response);
      } catch (erro: any) {
        console.error('Erro ao carregar dados da empresa:', erro);
        setError('Não foi possível carregar as informações da empresa.');
      } finally {
        setLoading(false);
      }
    };

    fetchDadosEmpresa();
  }, []);

  if (loading) {
    return (
      <div className={classes.container}>
        <div className={classes.content}>
          <Loader />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.container}>
        <div className={classes.policyCard}>
          <p style={{ color: '#d32f2f', textAlign: 'center' }}>{error}</p>
        </div>
      </div>
    );
  }

  const nomeEmpresa = empresa?.nome ?? process.env.REACT_APP_COMPANY;

  return (
    <div className={classes.container}>
      <div className={classes.policyCard}>
        <header>
          <h1 style={{ display: 'flex', justifyContent: 'center' }}>Política de Privacidade</h1>
        </header>

        <section>
          <p>
            A <strong>{nomeEmpresa}</strong> valoriza a sua privacidade e se compromete a proteger
            os dados pessoais dos usuários. Esta política descreve de forma transparente como
            coletamos, utilizamos, armazenamos e protegemos suas informações.
          </p>
        </section>

        <section>
          <h2>1. Informações Coletadas</h2>
          <p>
            Coletamos os seguintes dados pessoais fornecidos voluntariamente pelos usuários através
            de formulários e cadastros:
          </p>
          <ul>
            <li>Nome completo</li>
            <li>Endereço</li>
            <li>Telefone</li>
            <li>Endereço de e-mail</li>
          </ul>
          <p>
            Essas informações são essenciais para a criação de contas, processamento de pedidos e
            comunicação com nossos clientes.
          </p>
        </section>

        <section>
          <h2>2. Finalidade do Tratamento de Dados</h2>
          <p>As informações pessoais fornecidas são utilizadas exclusivamente para:</p>
          <ul>
            <li>Processar e gerenciar pedidos realizados através do aplicativo</li>
            <li>Manter o histórico de compras para melhor atendimento</li>
            <li>Facilitar a comunicação entre {nomeEmpresa} e o cliente</li>
            <li>Melhorar a experiência do usuário em nossos serviços</li>
          </ul>
        </section>

        <section>
          <h2>3. Compartilhamento de Dados</h2>
          <p>
            A {nomeEmpresa} <strong>não compartilha, vende ou comercializa</strong> as informações
            dos usuários com terceiros. Os dados são mantidos sob estrita confidencialidade e
            utilizados apenas para as finalidades descritas nesta política.
          </p>
        </section>

        <section>
          <h2>4. Armazenamento e Conservação de Dados</h2>
          <p>
            Os dados dos usuários são armazenados em servidores seguros com criptografia avançada
            por um período mínimo de <strong>5 anos</strong>, em conformidade com as obrigações
            legais aplicáveis. Após esse período, os dados poderão ser excluídos, exceto quando
            houver determinação legal para sua manutenção.
          </p>
        </section>

        <section>
          <h2>5. Direitos do Titular e Exclusão de Dados</h2>
          <p>
            Os usuários possuem o direito de solicitar a exclusão de seus dados pessoais a qualquer
            momento, conforme previsto na legislação aplicável. Para exercer esse direito, entre em
            contato conosco através do canal oficial:
          </p>
          <p>
            <a
              href="https://edilsystem.com.br/site/fale-conosco/"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://edilsystem.com.br/site/fale-conosco/
            </a>
          </p>
        </section>

        <section>
          <h2>6. Cookies e Tecnologias Similares</h2>
          <p>
            Nosso aplicativo <strong>não utiliza cookies</strong> ou quaisquer outras tecnologias de
            rastreamento para coleta de dados pessoais.
          </p>
        </section>

        <section>
          <h2>7. Integrações com Serviços de Terceiros</h2>
          <p>
            Para otimizar a funcionalidade e performance do aplicativo, utilizamos os seguintes
            serviços:
          </p>
          <ul>
            <li>
              <strong>Google Analytics</strong>: coleta dados anônimos para análise de uso e
              performance do aplicativo
            </li>
            <li>
              <strong>Google Firebase</strong>: fornece serviços de autenticação e armazenamento
              seguro de dados
            </li>
          </ul>
          <p>Para mais informações sobre as políticas desses serviços, consulte:</p>
          <ul>
            <li>
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Política de Privacidade do Google Analytics
              </a>
            </li>
            <li>
              <a
                href="https://firebase.google.com/support/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Política de Privacidade do Firebase
              </a>
            </li>
          </ul>
        </section>

        <section>
          <h2>8. Medidas de Segurança</h2>
          <p>
            Implementamos rigorosas medidas de segurança, incluindo{' '}
            <strong>criptografia de dados</strong>, <strong>controle de acesso</strong> e
            <strong> monitoramento contínuo</strong>, para proteger as informações dos usuários
            contra acessos não autorizados, alterações ou divulgações indevidas.
          </p>
        </section>

        <section>
          <h2>9. Atualizações da Política</h2>
          <p>
            Reservamo-nos o direito de atualizar esta política periodicamente para refletir mudanças
            em nossos processos ou requisitos legais. Alterações significativas serão comunicadas
            aos usuários através do aplicativo ou de nosso website.
          </p>
        </section>

        <section>
          <h2>10. Canal de Comunicação</h2>
          <p>
            Para esclarecimentos adicionais sobre esta Política de Privacidade ou sobre o tratamento
            de seus dados pessoais, entre em contato através de nosso canal oficial:
          </p>
          <p>
            <a
              href="https://edilsystem.com.br/site/fale-conosco/"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://edilsystem.com.br/site/fale-conosco/
            </a>
          </p>
        </section>
      </div>
    </div>
  );
};

export default PoliticasPrivacidadePage;
